import {
  TTheme,
  TThemeProviderProps,
  TThemeProviderState,
} from "@zeg/shared/app.types";
import {TooltipProvider} from "@zeg/ui/Tooltip";
import {createContext, useContext, useEffect, useState} from "react";

const ThemeProviderContext = createContext<TThemeProviderState | undefined>(
  undefined
);

export function ThemeProvider({
  children,
  storageKey = "zeg-theme",
}: TThemeProviderProps) {
  const [theme, setThemeState] = useState<TTheme>(() => {
    if (typeof window === "undefined") return "system";
    const savedTheme = localStorage.getItem(storageKey) as TTheme;
    return savedTheme || "system";
  });

  const [resolvedTheme, setResolvedTheme] = useState<"dark" | "light">(() => {
    if (typeof window === "undefined") return "light";
    return window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");

    const newTheme = theme === "system" ? resolvedTheme : theme;
    root.classList.add(newTheme);
  }, [theme, resolvedTheme]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = () => {
      const newResolvedTheme = mediaQuery.matches ? "dark" : "light";
      setResolvedTheme(newResolvedTheme);

      if (theme === "system") {
        document.documentElement.classList.remove("light", "dark");
        document.documentElement.classList.add(newResolvedTheme);
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [theme]);

  const setTheme = (newTheme: TTheme) => {
    localStorage.setItem(storageKey, newTheme);
    setThemeState(newTheme);
  };

  return (
    <ThemeProviderContext.Provider value={{theme, resolvedTheme, setTheme}}>
      <TooltipProvider delayDuration={200}>{children}</TooltipProvider>
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  if (!context) throw new Error("useTheme must be used within a ThemeProvider");
  return context;
};
